import React, { useState, useEffect } from 'react';
import _ from 'lodash';

interface WaiverMatchesByAirportProps {
  waivers: {
    yearMonths: string[];
    airports: string[];
    [key: string]: any;
  };
  tripMatches: {
    yearMonths: string[];
    [key: string]: any;
  };
}

interface AirportCounts {
  airport: string;
  counts: number[];
  totalWaivers: number;
  totalTripMatches: number;
}

interface SortOrder {
  field: string;
  reverse: boolean;
}

interface SortOption {
  name: string;
  field: string;
  reverse: boolean;
}

const WaiverMatchesByAirport: React.FC<WaiverMatchesByAirportProps> = ({ waivers, tripMatches }) => {
  const [allAirportCounts, setAllAirportCounts] = useState<AirportCounts[]>([]);
  const [sortOrder, setSortOrder] = useState<SortOrder>({
    field: 'totalWaivers',
    reverse: true,
  });
  const [sortOptions, setSortOptions] = useState<SortOption[]>([]);
  const [yearMonths, setYearMonths] = useState<string[]>([]);
  const [totalHeader, setTotalHeader] = useState<string>('');

  useEffect(() => {
    initialize();
  }, [waivers, tripMatches]);

  useEffect(() => {
    if (allAirportCounts.length > 0) {
      sortChart(allAirportCounts);
    }
  }, [sortOrder]);

  const generateAllAirportCounts = (): AirportCounts[] => {
    return waivers.airports.map((airport) => {
      const counts = showTotalColumn()
        ? generateYearMonthCountsForAirport(airport)
        : [];
      const totalWaivers = _.sum(waivers[airport]);
      const totalTripMatches = _.sum(tripMatches[airport] || []);
      return { airport, counts, totalWaivers, totalTripMatches };
    });
  };

  const generateYearMonthCountsForAirport = (airport: string): number[] => {
    return waivers.yearMonths.flatMap((yearMonth, index) => {
      const tripMatchIndex = tripMatches.yearMonths.indexOf(yearMonth);
      const airportTripMatches = tripMatches[airport] || [];
      return [
        waivers[airport][index] || 0,
        airportTripMatches[tripMatchIndex] || 0,
      ];
    });
  };

  const showTotalColumn = (): boolean => {
    return waivers.yearMonths.length > 1;
  };

  const sortChart = (counts: AirportCounts[]): void => {
    const sortChain = _(counts).sortBy(sortOrder.field);
    setAllAirportCounts(
      sortOrder.reverse ? sortChain.reverse().value() : sortChain.value(),
    );
  };

  const isSortedBy = (field: string): boolean => {
    return sortOrder.field === field;
  };

  const initialize = (): void => {
    setSortOptions([
      { name: 'Total Waivers', field: 'totalWaivers', reverse: true },
      { name: 'Airport', field: 'airport', reverse: false },
      { name: 'Trip Matches', field: 'totalTripMatches', reverse: true },
    ]);
    setYearMonths(showTotalColumn() ? waivers.yearMonths : []);
    setTotalHeader(showTotalColumn() ? 'Total' : waivers.yearMonths[0]);
    const counts = generateAllAirportCounts();
    setAllAirportCounts(counts);
    sortChart(counts);
  };

  return (
    <div className="col-md-12">
      <div className="waiver-airport-sort">
        <label htmlFor="sortOrder">Sort by:</label>
        <select
          className="form-control dropdown-toggle"
          id="sortOrder"
          value={sortOrder.field}
          onChange={(e) => {
            const selectedOption = sortOptions.find(
              (option) => option.field === e.target.value,
            );
            if (selectedOption) {
              setSortOrder(selectedOption);
            }
          }}
        >
          {sortOptions.map((option) => (
            <option key={option.field} value={option.field}>
              {option.name}
            </option>
          ))}
        </select>
      </div>
      <div className="waiver-airport-table">
        <table className="table table-striped table-hover">
          <thead>
            <tr>
              <th className="empty"></th>
              <th colSpan={2} className="header">
                {totalHeader}
              </th>
              {yearMonths.map((yearMonth, index) => (
                <th key={index} colSpan={2} className="header">
                  {yearMonth}
                </th>
              ))}
            </tr>
            <tr className="sub-header">
              <th>
                Airport
                <i
                  className={`sprites ${
                    isSortedBy('airport') ? 'sprite-arrow-ivory-up' : ''
                  }`}
                ></i>
              </th>
              <th>
                Waivers
                <i
                  className={`sprites ${
                    isSortedBy('totalWaivers') ? 'sprite-arrow-ivory-down' : ''
                  }`}
                ></i>
              </th>
              <th>
                Trips
                <i
                  className={`sprites ${
                    isSortedBy('totalTripMatches')
                      ? 'sprite-arrow-ivory-down'
                      : ''
                  }`}
                ></i>
              </th>
              {yearMonths.map((_, index) => (
                <React.Fragment key={index}>
                  <th>Waivers</th>
                  <th>Trips</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {allAirportCounts.map((airportCounts, index) => (
              <tr key={index}>
                <td className="airport">{airportCounts.airport}</td>
                <td>{airportCounts.totalWaivers}</td>
                <td>{airportCounts.totalTripMatches}</td>
                {airportCounts.counts.map((count, idx) => (
                  <td key={idx}>{count}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WaiverMatchesByAirport;